// "Call back" on the Missed calls page (owner request, PJ, 2026-10-24: "I want somewhere I could see all my missed calls
// so I can know who called at what time so I can call them back"). One press (the human action for this one call) puts
// the lead on the dial screen and dials the number they called from, through the normal gated prepare: do-not-call,
// calling hours and the 3-a-day cap still block, and "Call this number again?" still asks for a number called recently.
// The 2026-10-21 due-callback rule already treats a missed call as a callback they asked for (the missed-call or
// voicemail task: compliance/context.ts ASKED_AT_SQL), so the setter it rang dials it without the question; nothing here
// waives anything.
//   'lead'      openMembership, then dial() with the target the number is (owner's line / store line, missed.ts
//               callBackTarget); the lead stays on screen, not dialled, when that number is none of the lead's now.
//   'business'  openNoCampaign (a no-campaign number), the same.
//   'pad'       the dial pad's own no-campaign flow with the number filled in (manualDial.ts callFromPad): it finds a lead
//               holding the number or makes "Unknown business", asks "Call this number again?" when called recently, and
//               after the call the wrap-up asks to save it to My leads or not (never skipped).
import type { MissedCallRow } from '../../../src/shared/api-missed';
import { toast } from '../components/ui';
import { phone } from '../phone/manager';
import { callFromPad, type PadOutcome } from './manualDial';
import { callBackTarget } from './missed';
import { fmtPhone } from './labels';
import { dial, getDialer, openMembership, openNoCampaign } from './store';

export type MissedCallBackResult =
  | { kind: 'dialing' }
  /** The lead is on the dial screen, not dialled: `message` says why. */
  | { kind: 'opened'; message: string }
  /** "Call this number again?" is up (the pad's flow); its Yes dials. */
  | { kind: 'confirm' }
  /** Nothing dialled: `message` says why (a gate refusal, a call still owed, no number). */
  | { kind: 'problem'; message: string; reasons?: string[] };

const OWED = 'Finish the current call and log its outcome first.';

/** Call back the row's newest call. `go` navigates (injected so this stays testable). */
export async function callBackMissed(row: Pick<MissedCallRow, 'fromE164' | 'callBack' | 'callBackWhy'>, go: (path: string) => void): Promise<MissedCallBackResult> {
  const from = row.fromE164;
  const plan = row.callBack;
  if (!from || !plan) return { kind: 'problem', message: row.callBackWhy ?? 'There is no number to call back.' };
  const why = phone.whyCantCall();
  if (why) return { kind: 'problem', message: why };

  if (plan.how === 'pad') {
    const calling = () => toast(`Calling ${fmtPhone(from)} back with no campaign. After the call, save it to My leads or not.`, 'info');
    // onDialing runs when a "Call this number again?" Yes starts the call later; a call that starts now returns 'dialing'.
    const out: PadOutcome = await callFromPad({ phone: from, noCampaign: true, businessName: null, contactName: null }, go, { onDialing: calling });
    if (out.kind === 'dialing') {
      calling();
      return { kind: 'dialing' };
    }
    if (out.kind === 'confirm') return { kind: 'confirm' };
    if (out.kind === 'blocked') {
      return { kind: 'problem', message: `${out.businessName ? `${out.businessName}: not called.` : 'Not called.'}`, reasons: out.reasons.map((r) => r.message) };
    }
    return { kind: 'problem', message: out.message };
  }

  const opened = plan.how === 'lead' ? await openMembership(plan.membershipId) : await openNoCampaign(plan.businessId);
  if (opened === 'busy') { go('/'); return { kind: 'problem', message: OWED }; }
  if (opened === 'failed') return { kind: 'problem', message: "Couldn't open that lead. Try again." };
  go('/');
  const item = getDialer().item;
  if (!item) return { kind: 'problem', message: "Couldn't open that lead. Try again." };
  const t = callBackTarget(item, from);
  if (t.why !== null) {
    toast(t.why, 'warn');
    return { kind: 'opened', message: t.why };
  }
  // The press on Call back is the human action for this one call; dial() runs the normal gated prepare (and asks "Call
  // this number again?" itself when only the soft attempt rules stand in the way).
  void dial(t.target ? { target: t.target } : {});
  return { kind: 'dialing' };
}
